import { useEffect } from 'react';
import clsx from 'clsx';

import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import TextMessageImage from 'images/createAccount/TextMessage.svg';
import VoiceCallImage from 'images/createAccount/VoiceCall.svg';
import OktaImage from 'images/createAccount/OktaLogo.png';
import PhoneVerificationCard from '../PhoneVerificationCard';
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';

import usePhoneFactorRequest from '../hooks/usePhoneFactorRequest';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    display: 'flex',
    width: 484,
    backgroundColor: palette.cncUI.white,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.1)',
    marginTop: 50,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '40px 56px 0 50px',
    alignContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      paddingTop: 32,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    '& h1': {
      color: palette.cncUI.default['-9'],
    },
    '& p': {
      color: palette.cncUI.primary.gray['+0'],
    },
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      padding: '0 14px',
    },
  },
  tips: {
    boxShadow: 'none',
  },
  summary: {
    '& .MuiAccordionSummary-content': {
      flexGrow: 0,
    },
  },
  title: {
    fontWeight: 400,
    color: palette.cncUI.black,
    marginBottom: 8,
    [breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  description: {
    color: '#5c5c5c !important',
    [breakpoints.down('sm')]: {
      fontSize: 16,
    },
  },
  button: {
    width: 180,
    height: 40,
    textTransform: 'none',
    padding: 0,
    '& p': {
      color: palette.cncUI.white,
    },
    [breakpoints.down('sm')]: {
      marginTop: 25,
      marginBottom: 0,
    },
  },
  contained: {
    marginTop: 0,
    height: 40,
    '& span': {
      height: '100%',
      borderRadius: 2,
    },
    [breakpoints.down('sm')]: {
      marginTop: 8,
    },
  },
  buttonContainer: {
    [breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: `${palette.cncUI.default.background} !important`,
    color: palette.secondary.main,
    marginRight: 8,
  },
  backButton: {
    marginTop: 27,
    marginBottom: 47,
    textTransform: 'none',
    [breakpoints.down('sm')]: {
      marginTop: 49,
    },
  },
  method: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    justifyItems: 'center',
  },
  phone: {
    position: 'absolute',
    marginTop: -28,
    marginLeft: 330,
    color: `${palette.cncUI.primary.gray.primary} !important`,
    [breakpoints.down('sm')]: {
      marginLeft: 238,
    },
  },
}));

const AdditionalAuthentication = props => {
  const classes = useStyles();
  const factorRequest = usePhoneFactorRequest();
  const { setStep, changeState, isMobile, state } = props;
  const { tokenData } = state;

  useEffect(() => {
    window.onbeforeunload = function() {
      return true;
    };
    changeState({ backStep: 'AdditionalAuthentication' });
  }, []);

  const enroll = type => {
    factorRequest
      .enroll({
        type: type,
        loginId: tokenData.user.id,
        phone: state.phone && `+1${state.phone.replace(/\D/g, '')}`,
        extension: state.extension && state.extension.replace(/\D/g, ''),
        activate: true,
      })
      .then(() => {
        const newState = {
          call: type === 'call' ? true : state.call,
          sms: type === 'sms' ? true : state.sms,
          adding: type,
        };
        changeState(newState);
        setStep('AccountCreated');
      })
      .catch(err => {
        // TODO handle the errors
        Logger('accountCreation.additionalAuth').error('Enroll', err);
      });
  };

  return (
    <Paper id="AdditionalAuthenticationRoot" className={classes.root}>
      <Grid container spacing={1} className={classes.container}>
        <Grid item xs={12} lg={12}>
          <Typography variant="h4" className={classes.title}>
            Additional authentication methods
          </Typography>
          <Typography variant="body1" className={classes.description}>
            Select the Okta Verify method to verify your identity
          </Typography>
        </Grid>
        {!state.sms && (
          <>
            <Grid item xs={12} lg={12} className={classes.method}>
              <PhoneVerificationCard
                image={TextMessageImage}
                variant="small"
                title="Text Message (cell phone users only)"
                text="Receive a text message on your mobile device."
                extra={
                  isMobile
                    ? ''
                    : '(Text messages CANNOT be sent to a landline phone)'
                }
              />
            </Grid>
            {!state.extension && (
              <Grid item xs={12} lg={12} className={classes.buttonContainer}>
                <Button
                  id="AdditionalAuthenticationTextAddSameButton"
                  className={clsx(classes.button, classes.contained)}
                  onClick={() => enroll('sms')}
                >
                  <Typography variant="body2">Use this phone</Typography>
                </Button>
                {state.phone && (
                  <Typography
                    variant="body2"
                    className={classes.phone}
                  >{`(xxx) xxx-${state.phone.substr(
                    state.phone.length - 4,
                  )}`}</Typography>
                )}
              </Grid>
            )}
            <Grid item xs={12} lg={12} className={classes.buttonContainer}>
              <Button
                id="AdditionalAuthenticationTextAddDifferentButton"
                className={clsx(classes.button, classes.contained)}
                onClick={() => {
                  changeState({ adding: 'sms' });
                  setStep('TextMessage');
                }}
              >
                <Typography variant="body2">Add different phone</Typography>
              </Button>
            </Grid>
          </>
        )}
        {!state.call && (
          <>
            <Grid item xs={12} lg={12}>
              <PhoneVerificationCard
                image={VoiceCallImage}
                title="Voice"
                text="Receive a voice call on your mobile device or landline."
                variant="small"
              />
            </Grid>
            <Grid item xs={12} lg={12} className={classes.buttonContainer}>
              <Button
                id="AdditionalAuthenticationVoiceAddSameButton"
                className={clsx(classes.button, classes.contained)}
                onClick={() => enroll('call')}
              >
                <Typography variant="body2">Use this phone</Typography>
              </Button>
              {state.phone && (
                <Typography
                  variant="body2"
                  className={classes.phone}
                >{`(xxx) xxx-${state.phone.substr(
                  state.phone.length - 4,
                )}`}</Typography>
              )}
            </Grid>
            <Grid item xs={12} lg={12} className={classes.buttonContainer}>
              <Button
                id="AdditionalAuthenticationVoiceAddDifferentButton"
                className={clsx(classes.button, classes.contained)}
                onClick={() => {
                  changeState({ adding: 'call' });
                  setStep('VoiceCall');
                }}
              >
                <Typography variant="body2">Add different phone</Typography>
              </Button>
            </Grid>
          </>
        )}
        {!state.okta && (
          <Grid item xs={12} lg={12}>
            <PhoneVerificationCard
              image={OktaImage}
              title="Okta Verify (requires download)"
              text="Retrieve a code from an authentication app on your device."
              variant="okta"
            />
          </Grid>
        )}
        <Grid item xs={12} lg={12} className={classes.buttonContainer}>
          <Button
            id="AdditionalAuthenticationGoToSetupOktaVerifyButton"
            className={clsx(classes.button, classes.contained)}
            onClick={() => {
              setStep('OktaVerify');
            }}
          >
            <Typography variant="body2">Go to set-up</Typography>
          </Button>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Button
            tertiary
            id="AdditionalAuthenticationBackButton"
            className={classes.backButton}
            onClick={() => setStep('AccountCreated')}
          >
            <LeftArrow className={classes.leftArrow} />
            Back to review
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AdditionalAuthentication;
